import React, { useEffect, useRef, useState } from 'react';
import { Platform } from 'react-native';
import { Box, Text } from '@gluestack-ui/themed';

import { PRESENCE_PHRASE_CROSSFADE_MS } from './aiTutorPresenceState';

export type AiTutorPresenceShimmerVariant = 'active' | 'extendedWait';

type Props = {
  children: string;
  showShimmer: boolean;
  shimmerVariant?: AiTutorPresenceShimmerVariant;
  phraseKey: string;
  testID: string;
  size?: 'sm' | 'xs';
  color?: string;
  fontWeight?: 'normal' | 'medium' | 'semibold' | 'bold';
};

export const SHIMMER_PEAK_ALPHA = 0.9;
export const SHIMMER_SWEEP_MS = 1450;
export const SHIMMER_CYCLE_MS = 1750;
export const SHIMMER_PEAK_MS = SHIMMER_SWEEP_MS / 2;

const SHIMMER_BACKGROUND_SIZE_PERCENT = 185;
const SHIMMER_SWEEP_PERCENT = ((SHIMMER_SWEEP_MS / SHIMMER_CYCLE_MS) * 100).toFixed(2);
const SHIMMER_STOPS = [
  { position: 0, alpha: 0 },
  { position: 30, alpha: 0 },
  { position: 38, alpha: 0.34 },
  { position: 45, alpha: 0.72 },
  { position: 50, alpha: SHIMMER_PEAK_ALPHA },
  { position: 55, alpha: 0.72 },
  { position: 62, alpha: 0.34 },
  { position: 70, alpha: 0 },
  { position: 100, alpha: 0 },
] as const;

const SHIMMER_GRADIENT = `linear-gradient(
  90deg,
  ${SHIMMER_STOPS.map(
    ({ position, alpha }) => `rgba(255, 255, 255, ${alpha}) ${position}%`,
  ).join(',\n  ')}
)`;

function sampleShimmerAlpha(position: number): number {
  if (
    position <= SHIMMER_STOPS[0].position
    || position >= SHIMMER_STOPS[SHIMMER_STOPS.length - 1].position
  ) return 0;
  for (let index = 1; index < SHIMMER_STOPS.length; index += 1) {
    const right = SHIMMER_STOPS[index];
    if (position <= right.position) {
      const left = SHIMMER_STOPS[index - 1];
      const progress = (position - left.position) / (right.position - left.position);
      return left.alpha + (right.alpha - left.alpha) * progress;
    }
  }
  return 0;
}

export function measureShimmerVisualContrast(): {
  peakMeanGlyphLuminanceDelta: number;
  changedGlyphCoverage: number;
  troughMeanGlyphLuminanceDelta: number;
} {
  const samples = 400;
  const baseGlyphLuminance = 156;
  let totalDelta = 0;
  let changed = 0;
  const visibleGradientWidth = 100 / (SHIMMER_BACKGROUND_SIZE_PERCENT / 100);
  const gradientStart = (100 - visibleGradientWidth) / 2;

  for (let index = 0; index < samples; index += 1) {
    const textPosition = (index + 0.5) / samples;
    const gradientPosition = gradientStart + textPosition * visibleGradientWidth;
    const alpha = sampleShimmerAlpha(gradientPosition);
    const delta = (255 - baseGlyphLuminance) * alpha;
    totalDelta += delta;
    if (delta >= 8) changed += 1;
  }

  return {
    peakMeanGlyphLuminanceDelta: totalDelta / samples,
    changedGlyphCoverage: changed / samples,
    // At the calm/trough position the transparent gradient tail is over all glyphs.
    troughMeanGlyphLuminanceDelta: 0,
  };
}

const SHIMMER_STYLE_ID = 'ai-tutor-presence-text-shimmer-keyframes';

const SHIMMER_CLASS: Record<AiTutorPresenceShimmerVariant, string> = {
  active: 'ai-tutor-presence-text-shimmer-overlay-active',
  extendedWait: 'ai-tutor-presence-text-shimmer-overlay-extended',
};

function ensureShimmerKeyframes() {
  if (Platform.OS !== 'web' || typeof document === 'undefined') return;
  if (document.getElementById(SHIMMER_STYLE_ID)) return;

  const style = document.createElement('style');
  style.id = SHIMMER_STYLE_ID;
  style.textContent = `
    @keyframes aiTutorPresenceTextShimmerActive {
      0% { background-position: 120% 0; }
      ${SHIMMER_SWEEP_PERCENT}% { background-position: -120% 0; }
      100% { background-position: -120% 0; }
    }
    @keyframes aiTutorPresenceTextShimmerExtended {
      0% { background-position: 120% 0; }
      ${SHIMMER_SWEEP_PERCENT}% { background-position: -120% 0; }
      100% { background-position: -120% 0; }
    }
    .ai-tutor-presence-text-shimmer-overlay-active,
    .ai-tutor-presence-text-shimmer-overlay-extended {
      color: transparent !important;
      background-image: ${SHIMMER_GRADIENT};
      background-size: ${SHIMMER_BACKGROUND_SIZE_PERCENT}% 100%;
      background-repeat: no-repeat;
      -webkit-background-clip: text;
      background-clip: text;
      pointer-events: none;
      animation-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
      animation-iteration-count: infinite;
    }
    .ai-tutor-presence-text-shimmer-overlay-active {
      animation-name: aiTutorPresenceTextShimmerActive;
      animation-duration: ${SHIMMER_CYCLE_MS}ms;
    }
    .ai-tutor-presence-text-shimmer-overlay-extended {
      animation-name: aiTutorPresenceTextShimmerExtended;
      animation-duration: ${SHIMMER_CYCLE_MS}ms;
    }
  `;
  document.head.appendChild(style);
}

const AiTutorPresenceShimmerText: React.FC<Props> = ({
  children,
  showShimmer,
  shimmerVariant = 'active',
  phraseKey,
  testID,
  size = 'sm',
  color = '$textBody',
  fontWeight = 'medium',
}) => {
  const shouldRenderOverlay = showShimmer && Platform.OS === 'web';
  const previousPhraseRef = useRef(phraseKey);
  const [phraseOpacity, setPhraseOpacity] = useState(1);

  useEffect(() => {
    if (shouldRenderOverlay) ensureShimmerKeyframes();
  }, [shouldRenderOverlay]);

  useEffect(() => {
    if (previousPhraseRef.current === phraseKey || !showShimmer) {
      previousPhraseRef.current = phraseKey;
      setPhraseOpacity(1);
      return undefined;
    }

    setPhraseOpacity(0);
    const timer = setTimeout(() => {
      previousPhraseRef.current = phraseKey;
      setPhraseOpacity(1);
    }, PRESENCE_PHRASE_CROSSFADE_MS);

    return () => clearTimeout(timer);
  }, [phraseKey, showShimmer]);

  return (
    <Box position="relative" alignSelf="flex-start" maxWidth="100%">
      <Text
        size={size}
        color={color}
        fontWeight={fontWeight}
        testID={testID}
        position="relative"
      >
        {shouldRenderOverlay
          ? React.createElement(
            'span',
            {
              style: {
                opacity: phraseOpacity,
                transition: `opacity ${PRESENCE_PHRASE_CROSSFADE_MS}ms ease`,
              },
            },
            children,
          )
          : children}
        {shouldRenderOverlay && React.createElement(
          'span',
          {
            'data-testid': `${testID}-shimmer`,
            'data-shimmer-overlay': shimmerVariant,
            'data-shimmer-cycle-ms': SHIMMER_CYCLE_MS,
            'data-shimmer-sweep-ms': SHIMMER_SWEEP_MS,
            'data-shimmer-peak-ms': SHIMMER_PEAK_MS,
            'aria-hidden': true,
            className: SHIMMER_CLASS[shimmerVariant],
            style: {
              position: 'absolute',
              inset: 0,
              font: 'inherit',
              lineHeight: 'inherit',
              letterSpacing: 'inherit',
              whiteSpace: 'inherit',
              pointerEvents: 'none',
              color: 'transparent',
              backgroundImage: SHIMMER_GRADIENT,
              backgroundSize: `${SHIMMER_BACKGROUND_SIZE_PERCENT}% 100%`,
              backgroundRepeat: 'no-repeat',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              opacity: phraseOpacity,
              transition: `opacity ${PRESENCE_PHRASE_CROSSFADE_MS}ms ease`,
            },
          },
          children,
        )}
      </Text>
    </Box>
  );
};

export default AiTutorPresenceShimmerText;
